import {useState} from "react";
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Form,
	InputNumber,
	Radio,
	Row,
	Segmented,
	Slider,
	Space,
	Tag,
	Tooltip,
	Typography
} from "antd";
import {
	FileImageOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	ReloadOutlined,
	SaveOutlined,
	SettingOutlined
} from "@ant-design/icons";

import {normalizeWebpProfiles} from "../utils/webpProfiles.js";
import WebpProfileModal from "./WebpProfileModal.jsx";
import WebpProfilesTable from "./WebpProfilesTable.jsx";

const {Title, Text} = Typography;

export default function WebpSettingsTab({loading, saving, isDirty, onReload})
{
	const form = Form.useFormInstance();
	const [modalOpen, setModalOpen] = useState(false);
	const [editingProfile, setEditingProfile] = useState(null);

	const webpProfiles = Form.useWatch("webpProfiles", form) || [];
	const globalQuality = Form.useWatch(["webp", "quality"], form) || 80;
	const vectorDensityMode = Form.useWatch(["webp", "vectorDensityMode"], form) || "auto";
	const pageFormat = Form.useWatch(["flyerPages", "format"], form) || "webp";
	const ocrDpi = Form.useWatch(["flyerPages", "ocrDpi"], form) || 200;
	const exportDpi = Form.useWatch(["flyerPages", "exportDpi"], form) || 72;

	const handleAddProfile = () =>
	{
		setEditingProfile(null);
		setModalOpen(true);
	};

	const handleEditProfile = (profile) =>
	{
		setEditingProfile(profile);
		setModalOpen(true);
	};

	const handleSaveProfile = (profileData) =>
	{
		const currentProfiles = normalizeWebpProfiles(form.getFieldValue("webpProfiles") || []);

		if (profileData.id)
		{
			// Modifica esistente
			const updated = currentProfiles.map((p) =>
				p.id === profileData.id
					? normalizeWebpProfiles([{...p, ...profileData}])[0]
					: p
			);
			form.setFieldsValue({webpProfiles: normalizeWebpProfiles(updated)});
		}
		else
		{
			// Nuovo profilo
			const newProfile = {
				...profileData,
				id: `profile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
			};
			form.setFieldsValue({webpProfiles: normalizeWebpProfiles([...currentProfiles, newProfile])});
		}

		setModalOpen(false);
		setEditingProfile(null);

		// Salva automaticamente
		form.submit();
	};

	const handleDeleteProfile = (profileId) =>
	{
		const currentProfiles = normalizeWebpProfiles(form.getFieldValue("webpProfiles") || []);
		if (currentProfiles.length <= 1)
			return;

		const filtered = currentProfiles.filter((p) => p.id !== profileId);
		form.setFieldsValue({webpProfiles: normalizeWebpProfiles(filtered)});

		// Salva automaticamente
		form.submit();
	};

	const handleToggleDefault = (profileId) =>
	{
		const currentProfiles = normalizeWebpProfiles(form.getFieldValue("webpProfiles") || []);
		const updated = currentProfiles.map((p) => ({
			...p,
			isDefault: p.id === profileId ? !p.isDefault : false
		}));
		form.setFieldsValue({webpProfiles: normalizeWebpProfiles(updated)});

		// Salva automaticamente
		form.submit();
	};

	return (
		<div className="settings-tab-content">
			<Row gutter={[24, 24]}>
				<Col span={24}>
					{/* Card Impostazioni Globali */}
					<Card
						className="settings-content-card card-elev"
						actions={[
							<div key="actions" style={{display: "flex", justifyContent: "flex-end", gap: "8px", padding: "0 16px"}}>
								<Button
									icon={<ReloadOutlined spin={loading}/>}
									onClick={onReload}
									disabled={loading}
								>
									Ricarica
								</Button>
								<Button
									type="primary"
									htmlType="submit"
									icon={<SaveOutlined/>}
									loading={saving}
									disabled={!isDirty && !saving}
								>
									Salva
								</Button>
							</div>
						]}
					>
						<div className="settings-section-header">
							<div className="settings-section-icon-wrapper webp">
								<SettingOutlined className="settings-section-icon"/>
							</div>
							<div>
								<Title level={5} className="settings-section-title">
									Impostazioni Globali
								</Title>
								<Text type="secondary" className="settings-section-desc">
									Parametri globali per tutti i job di conversione
								</Text>
							</div>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>Qualità immagine</span>
										<Tooltip title="Valori più alti producono immagini migliori ma file più pesanti">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								extra="Bilanciamento tra qualità visiva e dimensione del file (1-100)"
							>
								<Row gutter={16} align="middle" wrap={false} style={{width: "100%"}}>
									<Col flex="auto" style={{minWidth: 0}}>
										<Form.Item name={["webp", "quality"]} noStyle>
											<Slider
												min={1}
												max={100}
												marks={{
													1: "Min",
													50: "50",
													80: "Consigliato",
													100: "Max"
												}}
												tooltip={{formatter: (v) => `${v}%`}}
											/>
										</Form.Item>
									</Col>
									<Col>
										<Form.Item name={["webp", "quality"]} noStyle>
											<InputNumber
												min={1}
												max={100}
												style={{width: 70}}
												controls={false}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>Livello di compressione</span>
										<Tooltip title="Effort determina quanto tempo dedicare alla compressione: valori più alti = file più piccoli ma elaborazione più lenta">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								extra="Compromesso tra velocità di elaborazione e compressione (0-6)"
							>
								<Row gutter={16} align="middle" wrap={false} style={{width: "100%"}}>
									<Col flex="auto" style={{minWidth: 0}}>
										<Form.Item name={["webp", "effort"]} noStyle>
											<Slider
												min={0}
												max={6}
												marks={{
													0: "Veloce",
													3: "Bilanciato",
													6: "Ottimale"
												}}
											/>
										</Form.Item>
									</Col>
									<Col>
										<Form.Item name={["webp", "effort"]} noStyle>
											<InputNumber
												min={0}
												max={6}
												style={{width: 70}}
												controls={false}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>Densità AI/PDF (DPI)</span>
										<Tooltip title="Controlla la rasterizzazione di file vettoriali (AI/PDF). Auto prova a calcolare un DPI adeguato al target per ridurre sfocature.">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								extra="I file raster (PNG/JPG/WebP) mantengono la risoluzione originale: questa impostazione impatta solo AI/PDF."
							>
								<Space direction="vertical" size={12} style={{width: "100%"}}>
									<Form.Item name={["webp", "vectorDensityMode"]} noStyle>
										<Segmented
											options={[
												{label: "Auto (consigliato)", value: "auto"},
												{label: "Manuale", value: "manual"}
											]}
										/>
									</Form.Item>

									{vectorDensityMode === "manual" ? (
										<Row gutter={16} align="middle" wrap={false} style={{width: "100%"}}>
											<Col flex="auto" style={{minWidth: 0}}>
												<Form.Item name={["webp", "vectorDensityDpi"]} noStyle>
													<Slider
														min={72}
														max={1200}
														step={1}
														marks={{
															72: "72",
															150: "150",
															300: "300",
															600: "600",
															1200: "1200"
														}}
													/>
												</Form.Item>
											</Col>
											<Col>
												<Form.Item name={["webp", "vectorDensityDpi"]} noStyle>
													<InputNumber
														min={72}
														max={1200}
														style={{width: 90}}
														controls={false}
													/>
												</Form.Item>
											</Col>
										</Row>
									) : (
										<Text type="secondary" className="settings-density-auto-note">
											Il sistema stima il DPI in base alla dimensione di output (canvas/resize) e usa 300 come fallback.
										</Text>
									)}
								</Space>
							</Form.Item>
						</div>

						<Divider style={{margin: "20px 0"}}/>

						<div className="settings-section-header" style={{marginBottom: 16}}>
							<div>
								<Space size={8} align="center" wrap>
									<Title level={5} className="settings-section-title" style={{margin: 0}}>
										Esportazione Pagine Flyer
									</Title>
									<Tag color="blue">OCR {ocrDpi} DPI</Tag>
									<Tag color="gold">XML {exportDpi} DPI</Tag>
								</Space>
								<Text type="secondary" className="settings-section-desc">
									Formato output delle pagine quando si carica un PDF (non influisce sulle risorse prodotto)
								</Text>
							</div>
						</div>

						<Alert
							type="info"
							showIcon
							className="settings-info-alert"
							message="DPI separati per qualità AI e compatibilità XML"
							description="Il DPI OCR genera pagine e crop più nitidi per l'elaborazione AI. Il DPI XML mantiene basse e costanti paginal, paginaa e coordinate esportate per i software esterni."
						/>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>DPI OCR pagine</span>
										<Tooltip title="Usato per rasterizzare il PDF caricato, visualizzare la pre-elaborazione e creare i crop inviati all'AI.">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								extra="Aumenta la leggibilità dei crop AI. Valori più alti producono file più pesanti e conversioni più lente."
							>
								<Row gutter={16} align="middle" wrap={false} style={{width: "100%"}}>
									<Col flex="auto" style={{minWidth: 0}}>
										<Form.Item name={["flyerPages", "ocrDpi"]} noStyle>
											<Slider
												min={72}
												max={300}
												step={1}
												marks={{
													72: "72",
													150: "150",
													200: "Consigliato",
													300: "300"
												}}
											/>
										</Form.Item>
									</Col>
									<Col>
										<Form.Item name={["flyerPages", "ocrDpi"]} noStyle>
											<InputNumber
												min={72}
												max={300}
												style={{width: 80}}
												controls={false}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>DPI XML/export</span>
										<Tooltip title="Usato per calcolare le dimensioni logiche e scalare le coordinate nell'XML esportato.">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
								extra="Mantiene paginal, paginaa e coordinate compatibili con il software esterno. 72 DPI conserva il comportamento attuale."
							>
								<Row gutter={16} align="middle" wrap={false} style={{width: "100%"}}>
									<Col flex="auto" style={{minWidth: 0}}>
										<Form.Item name={["flyerPages", "exportDpi"]} noStyle>
											<Slider
												min={72}
												max={300}
												step={1}
												marks={{
													72: "Attuale",
													150: "150",
													200: "200",
													300: "300"
												}}
											/>
										</Form.Item>
									</Col>
									<Col>
										<Form.Item name={["flyerPages", "exportDpi"]} noStyle>
											<InputNumber
												min={72}
												max={300}
												style={{width: 80}}
												controls={false}
											/>
										</Form.Item>
									</Col>
								</Row>
							</Form.Item>
						</div>

						<div className="settings-form-section">
							<Form.Item
								label={
									<Space>
										<span>Formato pagine</span>
										<Tooltip title="WebP offre file più leggeri. JPG è compatibile con più software di terze parti.">
											<InfoCircleOutlined className="settings-tooltip-icon"/>
										</Tooltip>
									</Space>
								}
							>
								<Form.Item name={["flyerPages", "format"]} noStyle>
									<Radio.Group>
										<Radio value="webp">WebP (predefinito)</Radio>
										<Radio value="jpg">JPG</Radio>
									</Radio.Group>
								</Form.Item>
							</Form.Item>
						</div>

						{pageFormat === "jpg" && (
							<div className="settings-form-section">
								<Form.Item
									label={
										<Space>
											<span>Qualità JPEG</span>
											<Tooltip title="Valori più alti producono immagini migliori ma file più pesanti">
												<InfoCircleOutlined className="settings-tooltip-icon"/>
											</Tooltip>
										</Space>
									}
									extra="Bilanciamento tra qualità visiva e dimensione del file (1-100)"
								>
									<Row gutter={16} align="middle" wrap={false} style={{width: "100%"}}>
										<Col flex="auto" style={{minWidth: 0}}>
											<Form.Item name={["flyerPages", "jpegQuality"]} noStyle>
												<Slider
													min={1}
													max={100}
													marks={{
														1: "Min",
														50: "50",
														85: "Consigliato",
														100: "Max"
													}}
													tooltip={{formatter: (v) => `${v}%`}}
												/>
											</Form.Item>
										</Col>
										<Col>
											<Form.Item name={["flyerPages", "jpegQuality"]} noStyle>
												<InputNumber
													min={1}
													max={100}
													style={{width: 70}}
													controls={false}
												/>
											</Form.Item>
										</Col>
									</Row>
								</Form.Item>
							</div>
						)}
						</Card>

					{/* Card Impostazioni Personalizzate */}
					<Card
						className="settings-content-card card-elev"
						style={{marginTop: 24}}
					>
						<div className="settings-section-header">
							<div className="settings-section-icon-wrapper webp">
								<FileImageOutlined className="settings-section-icon"/>
							</div>
							<div style={{flex: 1}}>
								<Title level={5} className="settings-section-title">
									Impostazioni Personalizzate
								</Title>
								<Text type="secondary" className="settings-section-desc">
									Configura profili di esportazione specifici per tipologie di volantino
								</Text>
							</div>
							<Button
								type="primary"
								icon={<PlusOutlined/>}
								onClick={handleAddProfile}
							>
								Aggiungi impostazione
							</Button>
						</div>

						<Divider style={{margin: "20px 0"}}/>

						<Form.Item name="webpProfiles" noStyle>
							<WebpProfilesTable
								profiles={webpProfiles}
								onEdit={handleEditProfile}
								onDelete={handleDeleteProfile}
								onToggleDefault={handleToggleDefault}
							/>
						</Form.Item>
					</Card>

				</Col>
			</Row>

			<WebpProfileModal
				open={modalOpen}
				profile={editingProfile}
				onSave={handleSaveProfile}
				onCancel={() => {
					setModalOpen(false);
					setEditingProfile(null);
				}}
				globalQuality={globalQuality}
			/>
		</div>
	);
}
